import { useState } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { Check, ChevronDown, Loader2, UserPlus } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { UserAvatar } from '@/components/shared/user-avatar'
import { useUpdateInquiry } from '@/stores/leads.store'
import { useOrganizationUsers } from '@/stores/organization.store'
import type { User } from '@/gen/cortado/v1/users_pb'
import { toast } from 'sonner'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ICON_SWAP_TRANSITION, usePendingWithMinimumVisibleDuration } from '@/hooks/use-pending-with-minimum-visible-duration'
import { cn, formatFullName } from '@/lib/utils'

interface InquiryAssigneeDropdownProps {
  inquiryId: string
  assignedTo?: string
  assignedToUser?: User
  className?: string
  /**
   * Avatar/icon-only trigger, for tight spaces like a Pipeline board card —
   * the assignee's name is dropped from the visible trigger but kept as its
   * accessible name via `aria-label`.
   */
  hideLabel?: boolean
  /**
   * `owner` renders a compact ghost button — "Lead Assignment · [avatar] Name ▾" or
   * "Lead Assignment · Assign ▾" — for page headers. Default is the bordered pill.
   */
  triggerVariant?: 'pill' | 'owner'
}

/**
 * Interactive pill that displays the inquiry's current assignee (or
 * "Unassigned") and allows changing it via a dropdown popover
 */
export function InquiryAssigneeDropdown({ inquiryId, assignedTo, assignedToUser, className, hideLabel = false, triggerVariant = 'pill' }: InquiryAssigneeDropdownProps) {
  const [open, setOpen] = useState(false)
  // The just-selected target, tracked locally so the trigger can show it
  // right away without waiting on a parent re-render from whichever cache
  // backs `assignedTo`/`assignedToUser` (ListJobs on the Pipeline table,
  // GetInquiry on the lead detail page — the latter has no optimistic patch
  // of its own). `undefined` = no pending selection; `null` = pending
  // "Unassigned"; otherwise the pending user id.
  const [pendingUserId, setPendingUserId] = useState<string | null | undefined>(undefined)
  const { updateInquiryAssignee, isLoading } = useUpdateInquiry()
  const { users } = useOrganizationUsers()
  const showLoader = usePendingWithMinimumVisibleDuration(isLoading)
  const prefersReducedMotion = useReducedMotion()

  const handleSelect = async (userId: string | null) => {
    if ((userId ?? '') === (assignedTo ?? '')) {
      setOpen(false)
      return
    }

    // Close immediately and show the pending selection right away — don't
    // wait on the mutation to resolve.
    setOpen(false)
    setPendingUserId(userId)

    try {
      await updateInquiryAssignee(inquiryId, userId)
      toast.success(userId ? 'Lead assigned' : 'Lead unassigned')
    } catch (error) {
      console.error('Failed to update inquiry assignee:', error)
      reportError(error, { feature: 'inquiry_assignee_update', kind: 'mutation' })
      toast.error('Failed to update assignee. Please try again.', {
        description: getErrorDescription(error),
      })
    } finally {
      // Whether it succeeded or failed, the source of truth (assignedTo /
      // assignedToUser props) has caught up by now — success sets it via the
      // response, failure via the cache rollback.
      setPendingUserId(undefined)
    }
  }

  const hasPendingTarget = isLoading && pendingUserId !== undefined
  const pendingUser = hasPendingTarget && pendingUserId
    ? users.find((orgUser) => orgUser.user?.id === pendingUserId)?.user
    : undefined
  const displayAssignedTo = hasPendingTarget ? (pendingUserId ?? '') : (assignedTo ?? '')
  const displayUser = hasPendingTarget ? pendingUser : assignedToUser
  const displayName = displayAssignedTo ? formatFullName(displayUser?.firstName, displayUser?.lastName) : 'Unassigned'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerVariant === 'owner' ? (
          <Button variant="ghost" size="sm" className={className} disabled={isLoading}>
            <span className="text-muted-foreground">Lead Assignment ·</span>
            {/* Same avatar ↔ loader crossfade as the lead stage control's dot. */}
            {(displayAssignedTo || showLoader) && (
              <span className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center">
                <AnimatePresence initial={false}>
                  {showLoader ? (
                    <motion.span
                      key="loader"
                      className="absolute inset-0 flex items-center justify-center"
                      initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.7 }}
                      transition={prefersReducedMotion ? { duration: 0 } : ICON_SWAP_TRANSITION}
                    >
                      <Loader2
                        role="status"
                        aria-label="Updating lead assignment"
                        className="h-4 w-4 animate-spin text-muted-foreground motion-reduce:animate-none"
                      />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="avatar"
                      className="absolute inset-0 flex items-center justify-center"
                      initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.7 }}
                      transition={prefersReducedMotion ? { duration: 0 } : ICON_SWAP_TRANSITION}
                    >
                      <UserAvatar firstName={displayUser?.firstName} lastName={displayUser?.lastName} size="sm" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
            )}
            {displayAssignedTo ? (
              <span className="text-foreground">{displayName}</span>
            ) : (
              <span className="text-muted-foreground">Assign</span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        ) : (
        <button
          className={cn(
            'inline-flex items-center gap-2 rounded-md border bg-background px-4 py-2 text-base font-medium transition-colors hover:bg-muted/50',
            isLoading && 'opacity-50 cursor-wait',
            className
          )}
          disabled={isLoading}
          aria-label={hideLabel ? displayName : undefined}
        >
          {displayAssignedTo ? (
            <>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserAvatar firstName={displayUser?.firstName} lastName={displayUser?.lastName} size="sm" />
              )}
              {!hideLabel && <span>{displayName}</span>}
            </>
          ) : (
            <>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {!hideLabel && <span>Unassigned</span>}
            </>
          )}
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Assign Lead</p>
          <Button
            variant="ghost"
            size="sm"
            className={cn('w-full justify-start gap-2 h-auto py-2', !assignedTo && 'bg-accent')}
            onClick={() => handleSelect(null)}
            disabled={!assignedTo}
          >
            <UserPlus className="h-4 w-4 flex-shrink-0" />
            <span>Unassigned</span>
            {!assignedTo && <Check className="ml-auto h-4 w-4 text-muted-foreground" />}
          </Button>
          {users.map((orgUser) => {
            const userId = orgUser.user?.id ?? ''
            const isCurrent = userId === assignedTo
            return (
              <Button
                key={userId}
                variant="ghost"
                size="sm"
                className={cn('w-full justify-start gap-2 h-auto py-2', isCurrent && 'bg-accent')}
                onClick={() => handleSelect(userId)}
                disabled={isCurrent}
              >
                <UserAvatar firstName={orgUser.user?.firstName} lastName={orgUser.user?.lastName} size="sm" />
                <span className="truncate">{formatFullName(orgUser.user?.firstName, orgUser.user?.lastName)}</span>
                {isCurrent && <Check className="ml-auto h-4 w-4 text-muted-foreground" />}
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
